import { useEffect, useState } from "react";
import { HeartHandshake, Compass } from "lucide-react";
import client from "../lib/api";
import { QuitGateModal } from "./QuitGateModal";
import { PresenceLine } from "./PresenceLine";
import { RoadmapCard } from "./RoadmapCard";
import { ReturnRitual } from "./ReturnRitual";
import { WelcomeToTheLoop, RefreshInvitation } from "./WelcomeToTheLoop";
import { YearTouch } from "./YearTouch";

/**
 * PledgeCard.
 *
 * The Home surface for a person's active pledge. Small on purpose:
 * the loop should stay in the background of a person's life, not on
 * top of it. Two verbs only: "check in" (opens a small form) and
 * "close this pledge" (opens the four humane exits).
 *
 * If the person has no active pledge, the card renders nothing so
 * the Home surface is unchanged for people who have not chosen to
 * pledge yet.
 */
const REACHABLE_LABEL = {
  reachable_with_support: "a reachable goal, with a little support",
  undershoots_ability: "a goal you can likely clear",
  unproven: "a goal worth testing",
  thin_signal: "still taking shape",
};

export function PledgeCard({ product }) {
  const [pledge, setPledge] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showQuit, setShowQuit] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await client.get("/accountability/pledge");
      const active = (data || []).find((p) => p.state === "active");
      setPledge(active || null);
    } catch (_e) { setPledge(null); }
  };

  const doCheckIn = async () => {
    if (!note.trim() || !pledge) return;
    setBusy(true); setStatus(null);
    try {
      const { data } = await client.post(
        `/accountability/pledge/${pledge.id}/check-in`,
        { answer_text: note.trim() },
      );
      setStatus(data);
      if (data.status === "recorded") setNote("");
      await load();
    } catch (_e) {
      setStatus({ error: "Klair could not save that just now. Please try again." });
    } finally { setBusy(false); }
  };
  if (!pledge) return null;

  return (
    <>
    <WelcomeToTheLoop pledgeId={pledge?.id} />
    <div className="k-card mt-8" data-testid="pledge-card">
      <ReturnRitual pledgeId={pledge?.id} />
      <div className="k-label mb-2">a goal you chose</div>
      <div className="flex items-start gap-3">
        <div className="k-chip mt-1"><Compass size={14} /></div>
        <div className="flex-1 min-w-0">
          <p className="klareet-serif" style={{ fontSize: "1.1rem", lineHeight: 1.4 }} data-testid="pledge-goal-text">
            {pledge.goal_text}
          </p>
          {pledge.reachable_tier && (
            <div className="k-quiet !text-xs mt-1" data-testid="pledge-tier">
              {REACHABLE_LABEL[pledge.reachable_tier] || "a goal worth going for"}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <div className="k-label mb-2">how did this week land</div>
        <textarea
          className="k-input klareet-serif"
          rows={3}
          maxLength={800}
          placeholder="one honest sentence is enough"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="pledge-checkin-input"
        />
        <div className="flex justify-between items-center mt-3">
          <button
            onClick={() => setShowQuit(true)}
            className="k-btn k-btn-ghost !text-xs"
            data-testid="pledge-close-btn"
          >
            close this pledge
          </button>
          <button
            onClick={doCheckIn}
            disabled={!note.trim() || busy}
            className="k-btn k-btn-primary"
            data-testid="pledge-checkin-submit"
          >
            {busy ? "Saving." : "Save check in"}
          </button>
        </div>
        {status?.status === "recorded" && (
          <div className="k-warm-card mt-3 p-3" data-testid="pledge-checkin-message">
            <p className="klareet-serif text-sm" style={{ lineHeight: 1.5 }}>{status.message}</p>
            {status.offer === "adjust_or_lower_or_pause" && (
              <div className="k-quiet !text-xs mt-2" data-testid="pledge-checkin-offer-3strike">
                you can adjust the shape of the pledge, lower the bar, or pause it. all of them are honest choices.
              </div>
            )}
            {status.offer === "pause_or_quit" && (
              <div className="k-quiet !text-xs mt-2" data-testid="pledge-checkin-offer-pause-or-quit">
                no urgency, no push. pause the pledge, or close it entirely, if that is what would help.
              </div>
            )}
          </div>
        )}
        {status?.status === "care_rail" && (
          <div className="k-warm-card mt-3 p-3 text-sm" data-testid="pledge-care-rail">
            <HeartHandshake size={14} className="inline mr-2" />
            {status.message}
          </div>
        )}
        {status?.error && (
          <div className="k-quiet mt-3 !text-xs" data-testid="pledge-error">
            {status.error}
          </div>
        )}
      </div>

      {showQuit && pledge && (
        <QuitGateModal
          pledgeId={pledge.id}
          onClose={() => setShowQuit(false)}
          onArchived={() => { setShowQuit(false); load(); }}
        />
      )}

      <PresenceLine pledgeId={pledge?.id} />
    </div>
    <RefreshInvitation pledgeId={pledge?.id} product={pledge?.product} />
    <YearTouch pledgeId={pledge?.id} product={pledge?.product} />
    <RoadmapCard pledge={pledge} />
    </>
  );
}
